import { Heading, Layout, TileSelect, TileSelectGroup } from '@vwfs-bronson/bronson-react';
import { FullContract, getQuickLinks, QuickLink, QuickLinkConditions, QuickLinkType } from './utils';
import React, { useCallback, useMemo, useRef } from 'react';
import {
    isBalloonLeasing,
    isLongTermOrOperatingLeasing,
    isCredit,
    isFinancialLease,
    isVDLContract,
    RequestStatus,
    hasUnpaidAmmount,
} from 'cpfr-common';
import { useTranslation } from 'react-i18next';
import { useIbanStatusTag } from '../../contracts/contract-details/financial-details/iban-section/consult-view/useIbanStatusTag';
import { useContactInformationChangeStatus } from '../../my-profile/useContactInformationChangeStatus';
import { usePaymentDayStatusTag } from '../../contracts/contract-details/financial-details/collection-date-section/non-edit-view/usePaymentDayStatusTag';
import { formatCpDate } from '@cp-shared-19/common-utilities';
import moment from 'moment';
import { getFirstEligibleDateForEarlyReturnPenalty } from '../../../utils/early-return-penalty-utils';

interface QuickLinksProps {
    isSMECustomer: boolean;
    selectedContracts: FullContract[];
    selectedQuickLink?: QuickLink;
    onHighlightQuickLink: (quickLink: QuickLink) => void;
    onConfirmQuickLink: (quickLink: QuickLink) => void;
}

const QuickLinks: React.FC<QuickLinksProps> = ({
    isSMECustomer,
    selectedQuickLink,
    selectedContracts,
    onHighlightQuickLink,
    onConfirmQuickLink,
}) => {
    const { t } = useTranslation('request');
    const { data: contactInformationChange, isLoading } = useContactInformationChangeStatus();
    const isIbanStatusTag = useIbanStatusTag();
    const isPaymentDayStatusTag = usePaymentDayStatusTag();
    const isContactChangePending = contactInformationChange?.requestCustomerStatus === RequestStatus.IN_PROGRESS;
    const lastsLongerThan12Months = (contract: FullContract) =>
        formatCpDate(contract.contractStartDate).add(12, 'month').toMoment().isBefore(new Date());
    const isStandalone = (contract: FullContract) => {
        return ['SENN_PR', 'SEPO_PR', 'SENO_PR', 'SEPN_PR', 'SEGN_PR'].includes(contract.productType || '');
    };
    const quickLinkConditions: QuickLinkConditions = useMemo(() => {
        return {
            [QuickLinkType.CHANGE_IBAN]: (contracts: FullContract[]) => {
                if (!contracts.length) return false;
                const { financial } = contracts[0];
                return (
                    !isIbanStatusTag &&
                    !isLoading &&
                    !!contactInformationChange?.requestCustomerStatus &&
                    !isContactChangePending &&
                    !!financial._links?.changeIban
                );
            },
            [QuickLinkType.CHANGE_COLLECTION_DATE]: (contracts: FullContract[]) => {
                if (!contracts.length || contracts.some(isStandalone)) return false;
                return !(
                    isPaymentDayStatusTag ||
                    (isContactChangePending && !!contactInformationChange?.requestCustomerStatus) ||
                    isSMECustomer ||
                    isVDLContract(contracts[0])
                );
            },
            [QuickLinkType.CHANGE_EMAIL_TELEPHONE]: () => true,
            [QuickLinkType.TOTAL_EARLY_SETTLEMENT]: (contracts: FullContract[]) => {
                if (
                    isSMECustomer ||
                    !contracts.length ||
                    (!!contactInformationChange?.requestCustomerStatus && isContactChangePending)
                )
                    return false;
                return contracts
                    .map(
                        (contract) =>
                            contract.isActive &&
                            (isCredit(contract.productType) ||
                                (isFinancialLease(contract.productType) && lastsLongerThan12Months(contract)) ||
                                (isBalloonLeasing(contract.productType) && lastsLongerThan12Months(contract))),
                    )
                    .every((v) => v === true);
            },
            [QuickLinkType.EARLY_RETURN_PENALTY]: (contracts: FullContract[]) => {
                if (
                    isSMECustomer ||
                    contracts.length !== 1 ||
                    (!!contactInformationChange?.requestCustomerStatus && isContactChangePending)
                )
                    return false;
                const contract = contracts[0];
                const isOperativeLeasing = isLongTermOrOperatingLeasing(contract.productType);
                if (
                    !(
                        contract.isActive &&
                        isOperativeLeasing &&
                        lastsLongerThan12Months(contract) &&
                        !contract.isLeasingSocial
                    )
                )
                    return false;

                const firstEligibleDate = getFirstEligibleDateForEarlyReturnPenalty(contract);
                const isReturnPenaltyDisabled =
                    formatCpDate(contract.contractEndDate).toMoment().month() === moment().month() ||
                    formatCpDate(contract.contractEndDate).toMoment().isBefore(new Date()) ||
                    !firstEligibleDate ||
                    isVDLContract(contract) ||
                    hasUnpaidAmmount(contract);
                return !isReturnPenaltyDisabled;
            },
            [QuickLinkType.BILLING_COPY_REQUEST]: (contracts: FullContract[]) => {
                if (!contracts.length || !isSMECustomer) return false;
                return contracts
                    .map((contract) => {
                        return (
                            contract.productType &&
                            [
                                'LCDN_FL',
                                'LLDN_FL',
                                'LLDO_FL',
                                'LOUN_FL',
                                'LOUO_FL',
                                'CBEQ_FL',
                                'CBVO_FL',
                                'CBPO_FL',
                                'CBPN_FL',
                                'CBVE_FL',
                            ].includes(contract.productType)
                        );
                    })
                    .every((v) => v === true);
            },
            [QuickLinkType.AUTHORIZATION_DEBIT]: (contracts: FullContract[]) => {
                if (!contracts.length || isSMECustomer || isIbanStatusTag) return false;

                const hasExpiredContract = contracts.find((contract) =>
                    formatCpDate(contract.contractEndDate).add(6, 'month').toMoment().isBefore(new Date()),
                );
                if (hasExpiredContract) return false;

                return contracts.every((contract) => {
                    return (
                        isCredit(contract.productType) ||
                        isBalloonLeasing(contract.productType) ||
                        isFinancialLease(contract.productType)
                    );
                });
            },
            [QuickLinkType.TAX_DOCUMENT]: (contracts: FullContract[]) => {
                if (!isSMECustomer || !contracts.length || contracts.some(isStandalone)) return false;

                const hasExpiredContract = contracts.find((contract) =>
                    formatCpDate(contract.contractEndDate).add(6, 'month').toMoment().isBefore(new Date()),
                );
                if (hasExpiredContract) return false;

                return true;
            },
            [QuickLinkType.CHANGE_DURATION_MILEAGE]: (contracts: FullContract[]) => {
                if (!isSMECustomer || !contracts.length || contracts.some(isStandalone)) return false;

                const hasExpiredContract = contracts.find((contract) =>
                    formatCpDate(contract.contractEndDate).add(6, 'month').toMoment().isBefore(new Date()),
                );
                if (hasExpiredContract) return false;

                return true;
            },
        };
    }, [
        contactInformationChange?.requestCustomerStatus,
        isLoading,
        isIbanStatusTag,
        isPaymentDayStatusTag,
        isSMECustomer,
    ]);

    const groupRef = useRef<HTMLDivElement>(null);

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent, currentIndex: number, quickLinks: QuickLink[]) => {
            let nextIndex: number | null = null;

            switch (event.key) {
                case 'Tab':
                    if (event.shiftKey) {
                        if (currentIndex > 0) {
                            event.preventDefault();
                            nextIndex = currentIndex - 1;
                        }
                    } else {
                        if (currentIndex < quickLinks.length - 1) {
                            event.preventDefault();
                            nextIndex = currentIndex + 1;
                        }
                    }
                    if (nextIndex === null) return;
                    break;
                case 'ArrowRight':
                case 'ArrowDown':
                    event.preventDefault();
                    nextIndex = (currentIndex + 1) % quickLinks.length;
                    break;
                case 'ArrowLeft':
                case 'ArrowUp':
                    event.preventDefault();
                    nextIndex = (currentIndex - 1 + quickLinks.length) % quickLinks.length;
                    break;
                case 'Home':
                    event.preventDefault();
                    nextIndex = 0;
                    break;
                case 'End':
                    event.preventDefault();
                    nextIndex = quickLinks.length - 1;
                    break;
                case 'Enter':
                case ' ':
                    event.preventDefault();
                    onConfirmQuickLink(quickLinks[currentIndex]);
                    return;
                default:
                    return;
            }

            if (nextIndex !== null) {
                onHighlightQuickLink(quickLinks[nextIndex]);
                const container = groupRef.current;
                if (container) {
                    const tiles = container.querySelectorAll<HTMLElement>('.c-tile-select');
                    const inputs = container.querySelectorAll<HTMLInputElement>(
                        'input[name="tile-select-group-quick-links"]',
                    );

                    // Dispatch mouseleave on current tile and mouseenter on next tile
                    // to trigger existing hover styles from the design system
                    tiles[currentIndex]?.dispatchEvent(new MouseEvent('mouseleave', { bubbles: true }));
                    tiles[nextIndex]?.dispatchEvent(new MouseEvent('mouseenter', { bubbles: true }));
                    tiles[nextIndex]?.dispatchEvent(new MouseEvent('mouseover', { bubbles: true }));

                    inputs[nextIndex]?.focus();
                }
            }
        },
        [onHighlightQuickLink, onConfirmQuickLink],
    );

    return (
        <Layout.Item>
            <Heading level={3}>{t('quick-links.paragraph')}</Heading>
            {!isLoading && (
                <div ref={groupRef}>
                    <TileSelectGroup
                        title={{ text: t('quick-links.paragraph') }}
                        layoutItemClassName="u-1/3 u-1/2@l u-1/1@s"
                        className={'u-text-center'}
                        testId={'tile-select-group-quick-links'}
                        groupType="radio"
                    >
                        {getQuickLinks(selectedContracts, quickLinkConditions, isSMECustomer).map(
                            (quickLink, index, arr) => (
                                <TileSelect
                                    data-testid={`tile-select-quick-links-${quickLink.id}`}
                                    checked={selectedQuickLink?.id === quickLink.id}
                                    icon={quickLink.icon}
                                    radioGroupName="tile-select-group-quick-links"
                                    title={t(quickLink.label)}
                                    key={quickLink.id}
                                    inputType="radio"
                                    inputProps={{
                                        tabIndex: selectedQuickLink
                                            ? selectedQuickLink.id === quickLink.id
                                                ? 0
                                                : -1
                                            : index === 0
                                            ? 0
                                            : -1,
                                        onKeyDown: (e: React.KeyboardEvent) => handleKeyDown(e, index, arr),
                                        'aria-label': t(quickLink.label),
                                    }}
                                    onClick={() => onConfirmQuickLink(quickLink)}
                                />
                            ),
                        )}
                    </TileSelectGroup>
                </div>
            )}
        </Layout.Item>
    );
};

export default QuickLinks;
